import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { getTreeChildren, getTreeRoots, searchNodes, type VaultNode } from '@/lib/api'
import { useVaultStore } from '@/stores/useVaultStore'
import { useAutoRefresh } from './useAutoRefresh'
import {
  ExtTag,
  formatWhen,
  groupChildren,
  NodeIcon,
  nodeTextClass,
  sortActiveBlocks,
  sortInactive,
  StatusPill,
  type ChildGroup,
} from './vaultUi'

/** Left-rail lazy tree over the vault_nodes projection. Roots load on mount;
 * children fetch on expand and cache. Client-side name filter for v1 search;
 * backend lexical/semantic is the planned upgrade. */
export function VaultTree() {
  const [roots, setRoots] = useState<VaultNode[]>([])
  const [childrenById, setChildrenById] = useState<Record<string, VaultNode[]>>({})
  const [loading, setLoading] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [searchResults, setSearchResults] = useState<VaultNode[]>([])
  const [searching, setSearching] = useState(false)
  // Search scope (the ":" picker) + filetype + semantic toggle.
  const [scopeDomain, setScopeDomain] = useState<{ id: string; name: string } | null>(null)
  const [scopeWorkstream, setScopeWorkstream] = useState<string | null>(null)
  const [scopeWsList, setScopeWsList] = useState<VaultNode[]>([])
  const [pickerMode, setPickerMode] = useState<'domain' | 'workstream' | null>(null)
  const [searchExt, setSearchExt] = useState('')
  const [semantic, setSemantic] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const toggleGroup = (key: string) =>
    setExpandedGroups((s) => {
      const n = new Set(s)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })

  const { showArchived, setShowArchived, query, setQuery, expandedIds, selectedNodeId, requestReveal, revealTarget, toggleExpanded, setExpanded } =
    useVaultStore()

  useEffect(() => {
    getTreeRoots()
      .then(setRoots)
      .catch((e) => setError(String(e)))
  }, [])

  // Background freshness: silently re-pull roots + the children of any expanded
  // node so newly-created blocks/cards show up in the tree without a reload.
  // Replaces cached children in place; expand state (in the store) is untouched.
  // Children are fetched SEQUENTIALLY, not all at once — firing one request per
  // expanded node concurrently every tick was a thundering herd that exhausted
  // the API's DB connection pool (QueuePool timeout → 500s across the app).
  useAutoRefresh(async () => {
    try {
      setRoots(await getTreeRoots())
    } catch { /* keep prior roots on transient failure */ }
    for (const id of expandedIds) {
      try {
        const kids = await getTreeChildren(id)
        setChildrenById((m) => ({ ...m, [id]: kids }))
      } catch { /* skip this node this tick */ }
    }
  })

  // Vault-wide search (debounced): name (lexical) or semantic, scoped by the
  // ":" domain/workstream picker + filetype. Replaces the lazy tree with a flat
  // results list. Skipped while the picker is open.
  const term = query.trim()
  const hasScope = Boolean(scopeDomain || scopeWorkstream || searchExt)
  useEffect(() => {
    if (pickerMode) return
    if (!term && !hasScope) {
      setSearchResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    const t = setTimeout(
      () => {
        searchNodes({
          q: term || undefined,
          domain: scopeDomain?.name,
          workstream: scopeWorkstream || undefined,
          ext: searchExt || undefined,
          semantic,
        })
          .then((r) => setSearchResults(r.results))
          .catch(() => setSearchResults([]))
          .finally(() => setSearching(false))
      },
      semantic ? 350 : 200
    )
    return () => clearTimeout(t)
  }, [term, scopeDomain, scopeWorkstream, searchExt, semantic, pickerMode, hasScope])

  const onSearchInput = (v: string) => {
    setQuery(v)
    if (v.trim() === ':') setPickerMode('domain')
    else if (pickerMode) setPickerMode(null)
  }
  const pickDomain = (d: VaultNode) => {
    setScopeDomain({ id: d.id, name: d.name })
    setScopeWorkstream(null)
    setQuery('')
    setPickerMode('workstream')
    getTreeChildren(d.id)
      .then((kids) => setScopeWsList(kids.filter((k) => k.node_type === 'workstream')))
      .catch(() => setScopeWsList([]))
  }
  const pickWorkstream = (w: VaultNode) => {
    setScopeWorkstream(w.name)
    setPickerMode(null)
  }
  const clearScope = () => {
    setScopeDomain(null)
    setScopeWorkstream(null)
    setScopeWsList([])
    setPickerMode(null)
  }

  const loadedRef = useRef<Set<string>>(new Set())
  const inflightRef = useRef<Set<string>>(new Set())
  // The selected row's DOM node, so we can scroll it into view when selection
  // changes (e.g. revealed deep in a long tree, or selected from elsewhere).
  const selectedRowRef = useRef<HTMLDivElement | null>(null)

  const loadChildren = useCallback(async (id: string) => {
    if (loadedRef.current.has(id) || inflightRef.current.has(id)) return
    inflightRef.current.add(id)
    setLoading((s) => new Set(s).add(id))
    try {
      const kids = await getTreeChildren(id)
      loadedRef.current.add(id)
      setChildrenById((c) => ({ ...c, [id]: kids }))
    } catch {
      /* leave uncached so a later expand retries */
    } finally {
      inflightRef.current.delete(id)
      setLoading((s) => {
        const n = new Set(s)
        n.delete(id)
        return n
      })
    }
  }, [])

  // Fetch children for any expanded node that isn't loaded yet — covers the
  // persisted-expansion restore on mount AND store-driven expansion (a reveal
  // from search / deep-link / Concierge expands ancestors we haven't fetched).
  useEffect(() => {
    expandedIds.forEach((id) => {
      if (!loadedRef.current.has(id)) loadChildren(id)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIds])

  // Keep the active row in view when the selection changes. Keyed on the id
  // alone (not on expansion churn) so manually expanding elsewhere doesn't yank
  // the scroll position. `nearest` avoids jumping when it's already visible.
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [selectedNodeId])

  const onToggle = (node: VaultNode) => {
    const willExpand = !expandedIds.includes(node.id)
    toggleExpanded(node.id)
    if (willExpand) loadChildren(node.id)
  }

  // Flat id→node lookup over roots + every loaded child set. Any node the user
  // can click in the tree is in here (it had to be revealed to be visible), so
  // its full ancestor chain resolves.
  const nodeById = useMemo(() => {
    const m = new Map<string, VaultNode>()
    roots.forEach((n) => m.set(n.id, n))
    Object.values(childrenById).forEach((kids) => kids.forEach((n) => m.set(n.id, n)))
    return m
  }, [roots, childrenById])

  // Walk parent_id up to the domain root; best-effort if a link isn't cached.
  const topDomainOf = useCallback(
    (id: string): string | null => {
      let cur = nodeById.get(id)
      if (!cur) return null
      while (cur.parent_id && nodeById.get(cur.parent_id)) cur = nodeById.get(cur.parent_id)!
      return cur.id
    },
    [nodeById]
  )
  // Ancestor ids of a node, domain → … → parent (excludes the node itself).
  const ancestorsOf = useCallback(
    (id: string): string[] => {
      const out: string[] = []
      let cur = nodeById.get(id)
      while (cur?.parent_id) {
        const p = nodeById.get(cur.parent_id)
        if (!p) break
        out.unshift(p.id)
        cur = p
      }
      return out
    },
    [nodeById]
  )

  // Fetch + cache a node's children once (used while descending an unloaded
  // branch). Returns the kids directly so callers don't wait on a state flush.
  const ensureChildren = useCallback(async (id: string): Promise<VaultNode[]> => {
    const kids = await getTreeChildren(id)
    loadedRef.current.add(id)
    setChildrenById((c) => ({ ...c, [id]: kids }))
    return kids
  }, [])

  // Ancestor ids of the target, domain → … → parent. Fast path walks the cached
  // tree (true for any node clicked in-tree). Fallback DESCENDS from roots by
  // domain/workstream name + path — needed when the branch isn't loaded yet
  // (a search hit, a deep-link, a Concierge selection).
  const resolveAncestors = async (target: VaultNode): Promise<string[]> => {
    const cached = ancestorsOf(target.id)
    if (cached.length > 0 || !target.parent_id) return cached
    const out: string[] = []
    const dom = roots.find((r) => r.name === target.domain)
    if (!dom) return cached
    out.push(dom.id)
    if (target.node_type === 'workstream') return out
    const domKids = await ensureChildren(dom.id)
    const ws = domKids.find((k) => k.node_type === 'workstream' && k.name === target.workstream)
    if (!ws) return out
    out.push(ws.id)
    if (target.node_type === 'card') {
      const wsKids = await ensureChildren(ws.id)
      const parentPath = target.path.split('/').slice(0, -1).join('/')
      const blk = wsKids.find((k) => k.path === parentPath)
      if (blk) out.push(blk.id)
    }
    return out
  }

  // Scoped accordion: when a node becomes the reveal target, expand its ancestor
  // path, KEEP whatever is already expanded inside its domain (navigating within
  // a branch stays stable), and collapse every other domain's subtree. Jumping
  // domains tidies the rest. Runs for every select surface via `requestReveal`.
  useEffect(() => {
    const target = revealTarget
    if (!target) return
    let cancelled = false
    void (async () => {
      const ancestors = await resolveAncestors(target)
      if (cancelled) return
      const newTop = ancestors[0] ?? target.id // domain root (or the node itself)
      const keep = useVaultStore.getState().expandedIds.filter((id) => topDomainOf(id) === newTop)
      const next = [...keep, ...ancestors]
      if (target.has_children) next.push(target.id)
      setExpanded(next)
      requestReveal(null) // consume so a manual expand elsewhere doesn't re-fold
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealTarget])

  const onSelect = (node: VaultNode) => requestReveal(node)

  const q = query.trim().toLowerCase()
  const matches = (n: VaultNode) => !q || n.name.toLowerCase().includes(q)
  const visible = (nodes: VaultNode[]) =>
    nodes.filter((n) => (showArchived || !n.is_archived) && matches(n))

  const renderNode = (node: VaultNode, depth: number) => {
    const expanded = expandedIds.includes(node.id)
    const isSelected = selectedNodeId === node.id
    const expandable = node.has_children
    return (
      <div key={node.id}>
        <div
          ref={isSelected ? selectedRowRef : undefined}
          className={`group flex cursor-pointer items-center gap-1 rounded px-1 py-[3px] text-[13px] transition-colors ${
            isSelected ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60'
          }`}
          style={{ paddingLeft: `${depth * 12 + 4}px` }}
          onClick={() => onSelect(node)}
        >
          <button
            className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation()
              if (expandable) onToggle(node)
            }}
          >
            {expandable ? (expanded ? '▾' : '▸') : ''}
          </button>
          <NodeIcon type={node.node_type} className="mr-0.5" />
          <span className={`truncate ${nodeTextClass(node.node_type)}`} title={node.name}>
            {node.name}
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-1 pl-2">
            {node.node_type === 'block' && (
              <>
                {formatWhen(node) && <span className="text-[10px] text-muted-foreground">{formatWhen(node)}</span>}
                {!node.is_archived && <StatusPill status={node.calendar_status} wrapped={node.block_wrapped} />}
              </>
            )}
            {node.node_type === 'card' && <ExtTag ext={node.ext} />}
            {node.is_archived && <span className="text-[10px] opacity-60">📦</span>}
          </span>
        </div>
        {expanded && (
          <div>
            {loading.has(node.id) && !childrenById[node.id] && (
              <div className="px-2 py-1 text-[11px] text-muted-foreground" style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}>
                loading…
              </div>
            )}
            {renderChildren(node, depth)}
          </div>
        )}
      </div>
    )
  }

  // Group a container's children into Active / Inactive / Archived (workstreams)
  // or Active / Cards / Archived (blocks); other nodes render flat.
  const renderChildren = (node: VaultNode, depth: number) => {
    const all = childrenById[node.id] || []
    if (node.node_type !== 'domain' && node.node_type !== 'workstream') {
      // A block's own children (cards) always show — an archived block's cards
      // are themselves archived, so the showArchived filter would wrongly hide
      // them and the block would look empty when unfolded. Query-match only.
      return all.filter(matches).map((k) => renderNode(k, depth + 1))
    }
    const filtered = all.filter(matches)
    // Workstream: Active blocks vs one collapsed "Inactive" fold (archived
    // blocks + loose/blockless cards). Domain keeps Active / Inactive / Archived.
    const groups: ChildGroup[] =
      node.node_type === 'workstream'
        ? [
            {
              key: 'active',
              label: 'Active',
              collapsed: false,
              nodes: sortActiveBlocks(filtered.filter((c) => c.node_type === 'block' && !c.is_archived)),
            },
            {
              key: 'inactive',
              label: 'Inactive',
              collapsed: true,
              nodes: sortInactive(
                filtered.filter((c) => c.name !== 'CONCIERGE' && !(c.node_type === 'block' && !c.is_archived))
              ),
            },
          ].filter((g) => g.nodes.length > 0)
        : groupChildren(node.node_type, filtered).filter((g) => g.nodes.length > 0)
    return groups.map((g) => {
      const gkey = `${node.id}:${g.key}`
      const open = !g.collapsed || expandedGroups.has(gkey) || (showArchived && g.collapsed)
      return (
        <div key={g.key}>
          <div
            onClick={() => g.collapsed && toggleGroup(gkey)}
            style={{ paddingLeft: `${(depth + 1) * 12 + 4}px` }}
            className={`flex items-center gap-1 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70 ${
              g.collapsed ? 'cursor-pointer hover:text-muted-foreground' : ''
            }`}
          >
            {g.collapsed && (
              <span className="inline-block w-3 transition-transform" style={{ transform: open ? 'rotate(90deg)' : 'none' }}>
                ▸
              </span>
            )}
            {g.label}
            <span className="opacity-60">{g.nodes.length}</span>
          </div>
          {open && g.nodes.map((k) => renderNode(k, depth + 1))}
        </div>
      )
    })
  }

  // Flat search-result row, with its domain/workstream context so it's locatable.
  const renderResultRow = (node: VaultNode) => (
    <button
      key={node.id}
      onClick={() => requestReveal(node)}
      className={`flex w-full flex-col gap-0.5 rounded px-2 py-1 text-left transition-colors hover:bg-muted/60 ${
        selectedNodeId === node.id ? 'bg-muted' : ''
      }`}
    >
      <div className="flex w-full items-center gap-1.5 text-[13px]">
        <NodeIcon type={node.node_type} />
        <span className={`truncate ${nodeTextClass(node.node_type)}`}>{node.name}</span>
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {node.node_type === 'card' && <ExtTag ext={node.ext} />}
          {node.node_type === 'block' && !node.is_archived && (
            <StatusPill status={node.calendar_status} wrapped={node.block_wrapped} />
          )}
          {node.is_archived && <span className="text-[10px] opacity-60">📦</span>}
        </span>
      </div>
      {node.node_type !== 'domain' && (node.domain || node.workstream) && (
        <div className="truncate pl-[22px] text-[10px] text-muted-foreground/60">
          {node.domain}
          {node.workstream && node.node_type !== 'workstream' ? ` / ${node.workstream}` : ''}
        </div>
      )}
    </button>
  )

  // Lexical results as a PRUNED TREE — keep the domain → workstream hierarchy,
  // just drop the branches with no match. (Semantic stays a ranked flat list.)
  const renderPrunedResults = (results: VaultNode[]) => {
    const domains = new Map<string, { node?: VaultNode; ws: Map<string, { node?: VaultNode; items: VaultNode[] }> }>()
    const ensureDomain = (d: string) => {
      if (!domains.has(d)) domains.set(d, { ws: new Map() })
      return domains.get(d) as { node?: VaultNode; ws: Map<string, { node?: VaultNode; items: VaultNode[] }> }
    }
    const ensureWs = (d: string, w: string) => {
      const dom = ensureDomain(d)
      if (!dom.ws.has(w)) dom.ws.set(w, { items: [] })
      return dom.ws.get(w) as { node?: VaultNode; items: VaultNode[] }
    }
    for (const n of results) {
      const d = n.domain ?? '—'
      if (n.node_type === 'domain') {
        ensureDomain(n.name).node = n
        continue
      }
      if (n.node_type === 'workstream') {
        ensureWs(d, n.name).node = n
        continue
      }
      ensureWs(d, n.workstream ?? '—').items.push(n)
    }
    return [...domains.keys()]
      .sort((a, b) => a.localeCompare(b))
      .map((dName) => {
        const dom = domains.get(dName)!
        return (
          <div key={dName}>
            <div
              onClick={() => dom.node && requestReveal(dom.node)}
              className={`flex items-center gap-1.5 px-2 py-1 text-[13px] font-semibold text-foreground ${dom.node ? 'cursor-pointer hover:bg-muted/40' : ''}`}
            >
              <NodeIcon type="domain" />
              {dName}
            </div>
            {[...dom.ws.keys()]
              .sort((a, b) => a.localeCompare(b))
              .map((wName) => {
                const ws = dom.ws.get(wName)!
                return (
                  <div key={wName}>
                    <div
                      onClick={() => ws.node && requestReveal(ws.node)}
                      className={`flex items-center gap-1.5 py-0.5 pl-5 pr-2 text-[12px] font-medium text-foreground/90 ${ws.node ? 'cursor-pointer hover:bg-muted/40' : ''}`}
                    >
                      <NodeIcon type="workstream" />
                      {wName}
                    </div>
                    {ws.items.map((it) => (
                      <button
                        key={it.id}
                        onClick={() => requestReveal(it)}
                        className={`flex w-full items-center gap-1.5 py-0.5 pl-9 pr-2 text-left text-[13px] hover:bg-muted/60 ${
                          selectedNodeId === it.id ? 'bg-muted' : ''
                        }`}
                      >
                        <NodeIcon type={it.node_type} />
                        <span className={`truncate ${nodeTextClass(it.node_type)}`}>{it.name}</span>
                        <span className="ml-auto flex shrink-0 items-center gap-1.5">
                          {it.node_type === 'card' && <ExtTag ext={it.ext} />}
                          {it.node_type === 'block' && !it.is_archived && (
                            <StatusPill status={it.calendar_status} wrapped={it.block_wrapped} />
                          )}
                          {it.is_archived && <span className="text-[10px] opacity-60">📦</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                )
              })}
          </div>
        )
      })
  }

  if (error) {
    return <div className="p-3 text-[12px] text-red-400">Failed to load tree: {error}</div>
  }

  const querying = Boolean(term || hasScope)

  const renderPicker = (items: VaultNode[], onPick: (n: VaultNode) => void, kind: 'domain' | 'workstream') => (
    <>
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/60">
        {kind === 'domain' ? 'Pick a domain' : `Workstreams in ${scopeDomain?.name ?? ''}`}
      </div>
      {[...items]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((it) => (
          <button
            key={it.id}
            onClick={() => onPick(it)}
            className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-[13px] hover:bg-muted/60"
          >
            <NodeIcon type={it.node_type} />
            <span className={`truncate ${nodeTextClass(it.node_type)}`}>{it.name}</span>
          </button>
        ))}
      {items.length === 0 && <div className="px-2 py-1 text-[12px] text-muted-foreground">none</div>}
    </>
  )

  return (
    <div className="flex flex-col">
      <div className="sticky top-0 z-10 flex flex-col gap-1.5 border-b border-border bg-background/95 px-2 py-1.5 backdrop-blur">
        <div className="flex items-center gap-1.5">
          {scopeDomain && (
            <span className="flex shrink-0 items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[11px] text-foreground">
              {scopeDomain.name}
              {scopeWorkstream ? ` / ${scopeWorkstream}` : ''}
              <button onClick={clearScope} className="text-muted-foreground hover:text-foreground" title="clear scope">
                ×
              </button>
            </span>
          )}
          <div className="relative min-w-0 flex-1">
            <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => onSearchInput(e.target.value)}
              placeholder={scopeDomain ? 'Search in scope…' : 'Filter…   (type : to scope)'}
              title={'AND (default) · OR · -term / NOT · *wildcard · "phrase"\nType : to scope to a domain → workstream'}
              className="w-full rounded border border-border bg-muted/40 py-1 pl-7 pr-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <select
            value={searchExt}
            onChange={(e) => setSearchExt(e.target.value)}
            className="rounded border border-border bg-muted/40 px-1 py-0.5 text-[11px] text-foreground focus:outline-none"
            title="filetype filter"
          >
            <option value="">all types</option>
            <option value="md">md</option>
            <option value="pdf">pdf</option>
            <option value="docx">docx</option>
            <option value="xlsx">xlsx</option>
            <option value="eml">eml</option>
            <option value="png">png</option>
          </select>
          <button
            onClick={() => setSemantic((s) => !s)}
            title="semantic search — by meaning, not name"
            className={`rounded border px-1.5 py-0.5 transition-colors ${
              semantic ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300' : 'border-border hover:text-foreground'
            }`}
          >
            ≈ semantic
          </button>
          <label className="ml-auto flex cursor-pointer items-center gap-1" title="show archived groups">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-foreground" />
            arch
          </label>
        </div>
      </div>
      <div className="flex flex-col gap-px py-1">
        {pickerMode === 'domain' ? (
          renderPicker(roots, pickDomain, 'domain')
        ) : pickerMode === 'workstream' ? (
          renderPicker(scopeWsList, pickWorkstream, 'workstream')
        ) : querying ? (
          searchResults.length > 0 ? (
            semantic ? (
              searchResults.map((n) => renderResultRow(n))
            ) : (
              renderPrunedResults(searchResults)
            )
          ) : (
            <div className="p-3 text-[12px] text-muted-foreground">{searching ? 'Searching…' : 'No matches'}</div>
          )
        ) : (
          <>
            {visible(roots).map((r) => renderNode(r, 0))}
            {roots.length === 0 && !error && (
              <div className="p-3 text-[12px] text-muted-foreground">Loading vault…</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
